import { Modal, Form, InputGroup } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { Input } from "../../Controls/Input";
import { Button } from "../../Controls/Button";
import { HeaderText } from "../../Controls/HeaderText";
import { useEffect, useState } from "react";
import { ServerTransferEnrolledIntegrationParty } from "../../shared/api/axios-client";
import { Select } from "../../Controls/Select";
import { axiosClient } from "../../shared/apiConfig";

interface AdminOperatorIntegrationModalProps {
    show: boolean;
    setShow: (show: boolean) => void;
    onSave: (data: ServerTransferEnrolledIntegrationParty) => void;
    initialData?: ServerTransferEnrolledIntegrationParty;
    integrationPartyId?: number;
    isPushFromThirdParty?: boolean;
    isPullFromThirdParty?: boolean;
    isPullByThirdParty?: boolean;
}

export default function AdminOperatorIntegrationEnrollModal({ show, setShow, onSave, initialData, integrationPartyId, isPushFromThirdParty, isPullFromThirdParty, isPullByThirdParty }: AdminOperatorIntegrationModalProps) {

    const { register, reset, formState: { errors }, handleSubmit, setValue, watch } = useForm<ServerTransferEnrolledIntegrationParty>({
        defaultValues: initialData
    });
    const [generatingToken, setGeneratingToken] = useState(false);
    const canGenerateToken = isPushFromThirdParty || isPullByThirdParty;
    const token = watch("token");

    useEffect(() => {
        reset(initialData);
    }, [initialData, reset]);

    function submit(data: ServerTransferEnrolledIntegrationParty) {
        onSave(data);
        setShow(false);
    }

    async function generateToken() {
        if (!integrationPartyId || !initialData?.facilityId) return;
        setGeneratingToken(true);
        try {
            const response = await axiosClient.post<{ token: string }>(
                `/integration-parties/${integrationPartyId}/facility/${initialData.facilityId}/generate-token`
            );
            setValue("token", response.data.token, { shouldDirty: true });
        } finally {
            setGeneratingToken(false);
        }
    }

    async function copyToken() {
        if (token) await navigator.clipboard.writeText(token);
    }

    return (
        <Modal show={show} centered onHide={() => setShow(false)}>
            <Form onSubmit={handleSubmit(submit)}>
                <Modal.Header closeButton>
                    <HeaderText>
                        Edit Integration Party
                    </HeaderText>
                </Modal.Header>
                <Modal.Body>
                    {!isPullByThirdParty && (
                    <Input
                        type="text"
                        label="External ID"
                        {...register("externalId")}
                        placeholder="External ID"
                        error={errors.externalId}
                    />
                    )}
                    {isPullByThirdParty && (
                    <Input
                        type="text"
                        label="External ID (locationId)"
                        {...register("externalId", { required: "Location ID is required." })}
                        placeholder="Location ID"
                        error={errors.externalId}
                    />
                    )}

                    <Input
                        type="text"
                        label="Token"
                        {...register("token")}
                        placeholder="Token"
                        error={errors.token}
                        append={canGenerateToken && (
                            <InputGroup.Text className="p-0">
                                <Button
                                    type="button"
                                    size="sm"
                                    className="border-0 rounded-0"
                                    loading={generatingToken}
                                    loadingText="Generating"
                                    onClick={generateToken}
                                >
                                    Generate
                                </Button>
                                <Button
                                    type="button"
                                    size="sm"
                                    className="border-0 rounded-0"
                                    disabled={!token}
                                    onClick={copyToken}
                                >
                                    Copy
                                </Button>
                            </InputGroup.Text>
                        )}
                        explanation={
                            isPullByThirdParty
                                ? "Partner must send this bearer token and this location id together. A mismatch returns 401 and no data. Generate, then Save. Saving a new token destroys the previous one. Partner docs: /docs/partner/activity on the API host."
                                : isPushFromThirdParty
                                ? "Share this bearer token with the partner. External ID is their locationId."
                                : isPullFromThirdParty
                                    ? "External ID is the partner facility ID. Token is OAuth client_id; API Secret is client_secret."
                                    : undefined
                        }
                    />

                    {!isPullByThirdParty && (
                    <Input
                        type="text"
                        label="API Secret"
                        {...register("apiSecret")}
                        placeholder="API Secret"
                        error={errors.apiSecret}
                    />
                    )}
                    <Select label="Status" {...register("isActive")}>
                        <option value="">Select Integration</option>
                        {[{ id: 'true', name: 'Active' }, { id: 'false', name: 'Inactive' }].map((integration) => (
                            <option key={integration.id} value={integration.id}>
                                {integration.name}
                            </option>
                        ))}
                    </Select>
                </Modal.Body>
                <Modal.Footer className="d-flex">
                    <Button className="flex-grow-1" onClick={() => setShow(false)}>Cancel</Button>
                    <Button className="flex-grow-1 btn-main" type="submit">Save</Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}